import { formatDate } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { AppSettings } from 'pac-api';
import { Observable } from 'rxjs';
import { map, mergeMap, shareReplay, startWith } from 'rxjs/operators';
import { AccountTrainingService } from 'src/app/account-training.service';
import { CartService } from 'src/app/cart.service';
import { empty } from 'src/app/helpers';
import { AsnInfomation, Association } from 'src/app/model/assoication';
import { OrganizationModel } from 'src/app/model/organization.model';
import { SchoolInfo, TrainingRecord } from 'src/app/model/training-record';
import { WizardMembershipSelection } from 'src/app/model/wizard.model';
import { WizardService } from 'src/app/wizard/wizard.service';

@Component({
  selector: 'app-school',
  templateUrl: './school.component.html',
  styleUrls: ['./school.component.less']
})
export class SchoolComponent implements OnInit {

  public faMagnifyingGlass = faMagnifyingGlass;

  @Input()
  public training: TrainingRecord;

  @Input()
  public parentGroup: UntypedFormGroup;

  @Input()
  public SchoolType: string;

  @Input()
  public PreviousRecord: TrainingRecord;

  @Input()
  public Selection: WizardMembershipSelection;

  public group: UntypedFormGroup;

  public Schools$: Observable<OrganizationModel[]>;

  public State: string | Object;

  public ForeignLoc = new Object();

  public ForeignOrg: OrganizationModel = new OrganizationModel(
    {
      'cst_key': '5e4e874a-c5b0-41a9-aa6b-c1390ea8cb7a',
      'cst_recno': 333460,
      'cst_phn_number_complete_dn': '(800)458-2267',
      'org_name': 'Foreign Education Program',
      'adr_line1': '25188 Genesee Trail Rd',
      'adr_city': 'Golden',
      'adr_state': 'CO',
      'adr_post_code': '80401-5702'
    });

  public Asn: Association;

  public AsnInfomation = AsnInfomation;

  public Level: 'Student' | 'Professional' | 'Associate' | 'Graduate' = 'Professional';

  public HasStudentPortals: boolean = false;
  public IsInstructorReferral: boolean = false;

  public get NoHowHeard(): boolean {
    return empty(this.wizard.Individual.cst_src_key);
  }

  public get IsStudentPortal(): boolean {
    return this.wizard.IsStudentPortal;
  }

  public get IsUpgrade(): boolean {
    if (this.cart.Data) {
      const memberships = this.cart.Data.ExistingMemberships.filter(mbr => mbr.mbr_asn_code === this.Asn);
      const student = memberships.some(mbr => mbr.mbt_code === 'Student' || mbr.mbt_code === 'Free Network');
      const cppa = memberships.some(mbr => mbr.mbt_code === 'Certified' || mbr.mbt_code === 'Professional' || mbr.mbt_code === 'Practitioner' || mbr.mbt_code === 'Associate');
      return student && !cppa;
    }

    return false;
  }

  public StateHasError: Observable<boolean>;
  public NameHasError: Observable<boolean>;
  public CityHasError: Observable<boolean>;
  public CourseLengthHasError: Observable<boolean>;
  public GraduationDateHasError: Observable<boolean>;
  public NameAttendedHasError: Observable<boolean>;

  public get InstructorHasError(): boolean {
    const Referral = this.group.get('Instructor Referral');
    if (Referral) {
      const InstructorName = Referral.get('InstructorName');
      if (InstructorName) {
        return InstructorName.touched && InstructorName.invalid;
      }
    }

    return false;
  }

  public get CourseLengthError(): boolean {
    if (this.Asn === 'ABMP' && this.Level === 'Professional') {
      const courseLength = this.parentGroup.get('CourseLength');
      const errors = courseLength.errors;
      if (errors && 'min' in errors && errors['min']) {
        return true;
      }
    }

    return false;
  }

  public get GradDateWarning(): boolean {
    if (this.Level === 'Professional') {
      const gradDate: UntypedFormControl = <UntypedFormControl>this.parentGroup.get('GraduationDate');
      if (gradDate.touched) {
        const errors = WizardService.PastDate(gradDate);
        if (errors && 'PastDate' in errors && errors['PastDate']) {
          return true;
        }
      }
    }

    return false;
  }

  public get NeedTranscript(): boolean {
    return this.parentGroup && 'License1' in this.parentGroup.controls ? this.parentGroup.get('License1').get('Exempt').value : false;
  }

  constructor(
    private service: AccountTrainingService,
    private wizard: WizardService,
    private cart: CartService,
    public settings: AppSettings,
  ) {
  }

  public ngOnInit(): void {
    this.group = new UntypedFormGroup({
      Key: new UntypedFormControl(null, []),
      Name: new UntypedFormControl(null, [Validators.required]),
      City: new UntypedFormControl({value: null, disabled: this.IsStudentPortal}, [Validators.required]),
      State: new UntypedFormControl(null, [Validators.required]),
      Zip: new UntypedFormControl({value: null, disabled: this.IsStudentPortal}, []),
      Phone: new UntypedFormControl({value: null, disabled: this.IsStudentPortal}, [])
    });

    this.Schools$ = Observable.create(observer => {
      if (this.State !== this.ForeignLoc) {
        observer.next(this.group.value.Name);
      }
    }).pipe(mergeMap((query: string) => this.service.Schools(<string>this.State, query)));

    if (this.parentGroup) {
      this.parentGroup.registerControl('School', this.group);

      if(this.IsStudentPortal) {
        this.parentGroup.controls['CourseLength'].disable();
        this.parentGroup.controls['GraduationDate'].disable();
      }

      this.Asn = this.parentGroup.value.Association;
      const selection = this.service.GetMembershipSelection(this.Asn);

      if (selection) {
        this.Level = selection.Level;
      }
    }

    if (this.training) {
      if (empty(this.training.School)) {
        this.training.School = new SchoolInfo();
      } else {
        this.group.reset(this.training.School);
        if (this.training.School.Key === this.ForeignOrg.cst_key) {
          this.State = this.ForeignLoc;
        } else {
          this.State = this.training.School.State;
          this.IsInstructorReferral = this.training.School.IsInstructorReferral;
        }
      }

      if (this.training.GraduationDate) {
        const format = 'MM/dd/yyyy';
        const graduationDate = formatDate(this.training.GraduationDate, format, 'en-US');
        this.parentGroup.get('GraduationDate').reset(graduationDate);
        this.parentGroup.get('GraduationDate').markAsTouched();
      }

      this.parentGroup.get('CourseLength').markAsTouched();
    }

    this.group.get('Name').valueChanges.subscribe(value => {
      if (this.group.value.Key) {
        if (this.training && this.training.School.Name === value) {
          return;
        }

        this.group.get('Key').setValue(null);
      }
    });

    for (const key of ['State', 'Name', 'City']) {
      const control: AbstractControl = this.group.get(key);
      this[key + 'HasError'] = control.statusChanges.pipe(
        map(status => status === 'INVALID'),
        startWith(control.status === 'INVALID'),
        shareReplay(1),
      );
    }

    for (const key of ['CourseLength', 'GraduationDate', 'NameAttended']) {
      const control: AbstractControl = this.parentGroup.get(key);
      this[key + 'HasError'] = control.statusChanges.pipe(
        map(status => status === 'INVALID'),
        startWith(control.status === 'INVALID'),
        shareReplay(1),
      );
    }
  }

  public SchoolOnSelect(event: TypeaheadMatch): void {
    if (event.item instanceof OrganizationModel) {
      this.State = event.item.adr_state;
      this.group.get('City').setValue(event.item.adr_city);
      this.group.get('State').setValue(event.item.adr_state);
      this.group.get('Zip').setValue(event.item.adr_post_code);
      this.group.get('Phone').setValue(event.item.cst_phn_number_complete_dn);
      this.group.get('Key').setValue(event.item.cst_key);
      this.IsInstructorReferral = event.item.IsInstructorReferral;
      this.HasStudentPortals = event.item.HasStudentPortals;
    } else {
      this.HasStudentPortals = false;
      this.IsInstructorReferral = false;
    }
  }

  public StateOnChange(event: Event): void {
    if (this.State === this.ForeignLoc) {
      this.group.get('Name').setValue(this.ForeignOrg.org_name);
      this.group.get('City').setValue(this.ForeignOrg.adr_city);
      this.group.get('State').setValue(this.ForeignOrg.adr_state);
      this.group.get('Zip').setValue(this.ForeignOrg.adr_post_code);
      this.group.get('Phone').setValue(this.ForeignOrg.cst_phn_number_complete_dn);
      this.group.get('Key').setValue(this.ForeignOrg.cst_key);
    } else {
      this.group.get('State').setValue(this.State);

      if (this.group.value.Name == 'Foreign Education Program') {
        this.group.get('Name').setValue(null);
        this.group.get('City').setValue(null);
        this.group.get('Zip').setValue(null);
        this.group.get('Phone').setValue(null);
      }
    }
  }

  public CopyPrevious(training: TrainingRecord): void {
    this.group.reset(training.School);

    if (this.parentGroup) {
      if (training.GraduationDate) {
        const format = 'MM/dd/yyyy';
        const graduationDate = formatDate(training.GraduationDate, format, 'en-US');
        this.parentGroup.get('GraduationDate').reset(graduationDate);
      } else {
        this.parentGroup.get('GraduationDate').reset(null);
      }

      this.parentGroup.get('NameAttended').reset(training.NameAttended);
      this.parentGroup.get('CourseLength').reset(training.CourseLength);
      this.parentGroup.get('GraduationDate').markAsTouched();
      this.parentGroup.get('CourseLength').markAsTouched();
    }

    if (training.School.Key === this.ForeignOrg.cst_key) {
      this.State = this.ForeignLoc;
    } else {
      this.State = training.School.State;
    }
  }
}
